import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';

// Subscription interfaces
export interface ISubscription {
  product: string;
  name: string;
  type: 'subscription' | 'tiered' | 'one-time';
  billingCycle: 'monthly' | 'yearly' | 'quarterly';
  baseFee: number;
  features: string[];
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  nextBillingDate?: string;
  status?: 'active' | 'inactive' | 'cancelled' | 'expired';
}

@Component({
  selector: 'app-account-subscriptions',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    BadgeModule
  ],
  templateUrl: './account-subscriptions.html',
  styleUrl: './account-subscriptions.css'
})
export class AccountSubscriptions implements OnInit {
  subscriptions: ISubscription[] = [
    {
      product: 'core',
      name: 'Core Basic',
      type: 'subscription',
      billingCycle: 'monthly',
      baseFee: 3000,
      features: ['Access to core features', 'Basic reporting', 'Email support', 'Up to 5 users'],
      isActive: true,
      startDate: '2024-01-01',
      nextBillingDate: '2024-02-01',
      status: 'active'
    },
    {
      product: 'pulse',
      name: 'Pulse Basic',
      type: 'subscription',
      billingCycle: 'monthly',
      baseFee: 2000,
      features: ['Scheme management', 'Basic insights', 'Dashboard analytics', 'API access'],
      isActive: true,
      startDate: '2024-01-15',
      nextBillingDate: '2024-02-15',
      status: 'active'
    },
    {
      product: 'instagram',
      name: 'Instagram Starter',
      type: 'tiered',
      billingCycle: 'monthly',
      baseFee: 500,
      features: ['1 Instagram account', 'Basic automation', 'Post scheduling', 'Basic analytics'],
      isActive: true,
      startDate: '2024-01-20',
      nextBillingDate: '2024-02-20',
      status: 'active'
    },
    {
      product: 'analytics',
      name: 'Analytics Pro',
      type: 'subscription',
      billingCycle: 'yearly',
      baseFee: 12000,
      features: ['Advanced analytics', 'Custom reports', 'Data export', 'Priority support'],
      isActive: false,
      startDate: '2023-06-01',
      endDate: '2024-06-01',
      status: 'expired'
    }
  ];

  selectedSubscription: ISubscription | null = null;
  drawerOpen = false;

  // Filter and search
  searchTerm = '';
  filterType = 'all';
  filterStatus = 'all';

  // Filter options
  typeOptions = [
    { label: 'All Types', value: 'all' },
    { label: 'Subscription', value: 'subscription' },
    { label: 'Tiered', value: 'tiered' },
    { label: 'One-time', value: 'one-time' }
  ];

  statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'Expired', value: 'expired' }
  ];

  ngOnInit() {
    // Initialize component
  }

  get filteredSubscriptions(): ISubscription[] {
    return this.subscriptions.filter(sub => {
      const matchesSearch = sub.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                          sub.product.toLowerCase().includes(this.searchTerm.toLowerCase());
      const matchesType = this.filterType === 'all' || sub.type === this.filterType;
      const matchesStatus = this.filterStatus === 'all' || sub.status === this.filterStatus;
      
      return matchesSearch && matchesType && matchesStatus;
    });
  }

  selectSubscription(subscription: ISubscription) {
    this.selectedSubscription = subscription;
    this.drawerOpen = true;
  }

  closeDrawer() {
    this.drawerOpen = false;
    this.selectedSubscription = null;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getTypeBadgeClass(type: string): string {
    switch (type) {
      case 'subscription': return 'bg-blue-100 text-blue-800';
      case 'tiered': return 'bg-purple-100 text-purple-800';
      case 'one-time': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getBillingCycleClass(cycle: string): string {
    switch (cycle) {
      case 'monthly': return 'bg-sky-100 text-sky-800';
      case 'yearly': return 'bg-green-100 text-green-800';
      case 'quarterly': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getTotalActiveSubscriptions(): number {
    return this.subscriptions.filter(sub => sub.isActive).length;
  }

  getTotalMonthlyRevenue(): number {
    return this.subscriptions
      .filter(sub => sub.isActive && sub.billingCycle === 'monthly')
      .reduce((total, sub) => total + sub.baseFee, 0);
  }

  getTotalYearlyRevenue(): number {
    return this.subscriptions
      .filter(sub => sub.isActive && sub.billingCycle === 'yearly')
      .reduce((total, sub) => total + sub.baseFee, 0);
  }
}